import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../assets/icons/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import { CrossfadeText } from '../../components/motion/index';
import { useFormat, useSelectedCompany } from '../../hooks/index';
import { PageRoute } from '../../types/page.types';
import { Snapshot } from '../../types/snapshot.types';
import { toMathRound } from '../../utils/to-math-round.util';
import { toNumber } from '../../utils/to-number.util';
import { SummaryItem } from './summary-item';

export type OverAllStatusProps = {
  isLoadingFinished: boolean;
  shouldNavigate?: boolean;
  snapshot?: Snapshot;
  snapshotPanelKey?: string;
  wrapperClassName?: string;
};

export const OverAllStatus: FC<OverAllStatusProps> = ({
  wrapperClassName,
  snapshot,
  shouldNavigate = false,
  isLoadingFinished,
  snapshotPanelKey = 'latest',
}) => {
  const { format } = useFormat();
  const navigate = useNavigate();
  const { selectedCompany } = useSelectedCompany();
  const currency = selectedCompany?.currency;

  // People, not records, the rule the shared link's tile counts by (`countHoldingStakeholders`):
  // one person can hold equity under one stakeholder record and an investment under another, so
  // qualifying records — a whole share or money in — are collapsed on the name they carry.
  const snapshotStakeholdersCount = new Set(
    (snapshot?.stats?.stakeholders ?? [])
      .filter(
        (stakeholder) => toNumber(stakeholder.diluted) >= 1 || toNumber(stakeholder.invested) > 0,
      )
      .map(({ id, name }) => name?.trim().toLowerCase() || id),
  ).size;

  const isCapTableEmpty = !snapshot?.dilutedShares && !snapshot?.issuedShares;
  const randomNumber = useMemo(
    () => (isCapTableEmpty ? Math.random() * 100000 : 0).toLocaleString('en-US'),
    [isCapTableEmpty],
  );

  const crossfadeKey = `${snapshotPanelKey}-${isCapTableEmpty ? 'empty' : 'data'}`;

  return (
    <div
      className={twMerge(
        'flex h-full min-h-full flex-col items-center rounded-lg bg-gray-50 p-1',
        wrapperClassName,
        isCapTableEmpty && 'centered relative',
      )}
    >
      {isCapTableEmpty && isLoadingFinished && (
        <span className="absolute inset-0 z-1 flex items-center justify-center text-center text-label-md font-medium text-gray-500">
          <AppFormattedMessage id="CAP_TABLE_SNAPSHOT_WILL_BE_DISPLAYED_HERE" />
        </span>
      )}
      <div className="flex w-full items-start justify-between gap-4 px-4 py-3">
        <span className="w-full text-left text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
          <AppFormattedMessage id="OVERALL_STATUS" />
        </span>
        {shouldNavigate && (
          <ChevronDownIcon
            className="-rotate-90 transform cursor-pointer"
            onClick={() => navigate(PageRoute.CAP_TABLE)}
          />
        )}
      </div>

      <div
        className={twMerge(
          'flex h-full min-h-0 w-full flex-1 flex-wrap content-start gap-3 rounded-md bg-white p-3 shadow-sm max-lg:flex-col',
          isCapTableEmpty && 'select-none blur-md',
        )}
      >
        <SummaryItem
          title={<AppFormattedMessage id="POST_MONEY_VALUATION" />}
          value={
            !isCapTableEmpty ? (
              <CrossfadeText
                className="text-base font-semibold text-gray-600"
                textKey={`${crossfadeKey}-post-money`}
              >
                {`${currency?.symbol || currency?.iso3?.toLocaleUpperCase() || ''}${toMathRound(snapshot?.postMoneyValuation).toLocaleString('en-US')}`}
              </CrossfadeText>
            ) : (
              randomNumber
            )
          }
        />
        <SummaryItem
          title={<AppFormattedMessage id="VALUATION_DATE" />}
          value={
            !isCapTableEmpty ? (
              <CrossfadeText
                className="text-base font-semibold text-gray-600"
                textKey={`${crossfadeKey}-date`}
              >
                {format(snapshot?.valuation?.date, 'dd/MM/yyyy') || '-'}
              </CrossfadeText>
            ) : (
              format(new Date(), 'dd/MM/yyyy')
            )
          }
        />

        <SummaryItem
          title={<AppFormattedMessage id="LIFETIME_EQUITY_INVESTMENT" />}
          value={
            !isCapTableEmpty ? (
              <CrossfadeText
                className="text-base font-semibold text-gray-600"
                textKey={`${crossfadeKey}-lifetime`}
              >
                {`${currency?.symbol || currency?.iso3?.toLocaleUpperCase() || ''}${toNumber(snapshot?.lifetimeInvestmentEquity).toLocaleString('en-US')}`}
              </CrossfadeText>
            ) : (
              randomNumber
            )
          }
        />
        <SummaryItem
          title={<AppFormattedMessage id="ISSUED_SHARES" />}
          value={
            !isCapTableEmpty ? (
              <CrossfadeText
                className="text-base font-semibold text-gray-600"
                textKey={`${crossfadeKey}-issued`}
              >
                {toNumber(snapshot?.issuedShares).toLocaleString('en-US')}
              </CrossfadeText>
            ) : (
              randomNumber
            )
          }
        />

        <SummaryItem
          title={<AppFormattedMessage id="INVESTORS" />}
          value={
            !isCapTableEmpty ? (
              <CrossfadeText
                className="text-base font-semibold text-gray-600"
                textKey={`${crossfadeKey}-investors`}
              >
                {toNumber(snapshot?.investorsCount).toLocaleString('en-US')}
              </CrossfadeText>
            ) : (
              randomNumber
            )
          }
        />

        <SummaryItem
          title={<AppFormattedMessage id="STAKEHOLDERS" />}
          value={
            !isCapTableEmpty ? (
              <CrossfadeText
                className="text-base font-semibold text-gray-600"
                textKey={`${crossfadeKey}-stakeholders`}
              >
                {toNumber(snapshotStakeholdersCount).toLocaleString('en-US')}
              </CrossfadeText>
            ) : (
              randomNumber
            )
          }
        />

        <SummaryItem
          title={<AppFormattedMessage id="DILUTED_SHARES" />}
          value={
            !isCapTableEmpty ? (
              <CrossfadeText
                className="text-base font-semibold text-gray-600"
                textKey={`${crossfadeKey}-diluted`}
              >
                {toNumber(snapshot?.dilutedShares).toLocaleString('en-US')}
              </CrossfadeText>
            ) : (
              randomNumber
            )
          }
        />
      </div>
    </div>
  );
};
